import type { ComponentProps } from 'react';
import React from 'react';
import { StyleSheet } from 'react-native';
import { LI, UL } from '@expo/html-elements';

import {
  LocationResultCardLoading,
  LocationResultsCard,
} from '../../../LocationResultCard';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsList = (props: LocationResultsListProps) => {
  const {
    locations,
    locationSearchType,
    withDetailsModal,
    activeReorderRestaurantSlug,
    onCardPress,
    onCardLayout,
    onCardUnmount,
    isLoading,
  } = props;

  // ─── Loading View ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <UL>
        {CARD_LOADING_PLACEHOLDERS.map((placeholder, index) => (
          <LI key={placeholder} style={styles.listItem}>
            <LocationResultCardLoading withTopBorder={index > 0} />
          </LI>
        ))}
      </UL>
    );
  }

  // ─── Loaded View ─────────────────────────────────────────────────────

  return (
    <UL>
      {locations.map((location, index) => {
        const { id, restaurantSlug } = location;
        const isDisabled =
          activeReorderRestaurantSlug !== undefined &&
          restaurantSlug !== undefined &&
          activeReorderRestaurantSlug === restaurantSlug;

        return (
          <LI key={id} style={styles.listItem}>
            <LocationResultsCard
              location={location}
              withTopBorder={index > 0}
              locationSearchType={locationSearchType}
              withDetailsModal={withDetailsModal}
              isDisabled={isDisabled}
              onLayout={onCardLayout}
              onUmount={onCardUnmount}
              onPress={onCardPress}
            />
          </LI>
        );
      })}
    </UL>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_LOADING_PLACEHOLDERS = [1, 2, 3, 4, 5];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  listItem: {
    paddingHorizontal: 12,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListProps = {
  readonly locations: ReadonlyArray<LocationResultCardProps['location']>;
  readonly locationSearchType: 'pickup' | 'delivery' | 'outpost';
  readonly isLoading?: boolean;
  readonly activeReorderRestaurantSlug?: string;
  readonly onCardPress: LocationResultCardProps['onPress'];
  readonly onCardLayout: LocationResultCardProps['onLayout'];
  readonly onCardUnmount?: LocationResultCardProps['onUmount'];
} & Pick<LocationResultCardProps, 'withDetailsModal'>;

type LocationResultCardProps = ComponentProps<typeof LocationResultsCard>;
